<script>
import EcButton from '../ui/EcButton.vue'
import EcDrawer from '../ui/EcDrawer.vue'
import EcSpinner from '../ui/EcSpinner.vue'
import ModuleCredentialEditor from './ModuleCredentialEditor.vue'
import ModuleChildService from './ModuleChildService.vue'

export default {
  name: 'ModuleCard',
  components: { EcButton, EcDrawer, EcSpinner, ModuleCredentialEditor, ModuleChildService },
  props: {
    service: { type: Object, required: true },
    organization: { type: Object, required: true },
    allServices: { type: Array, default: () => [] },
    refreshingServiceId: { type: [String, Number], default: null },
  },
  data() {
    return {
      drawerOpen: false,
      loading: this.refreshingServiceId != null && this.refreshingServiceId === this.service.id,
    }
  },
  computed: {
    t() {
      return this.$ecTranslate || ((k) => k)
    },
    isParentService() {
      return this.service.isParent === true
    },
    childServices() {
      if (!this.isParentService || !this.allServices) return []
      return this.allServices.filter((s) => s.parent === this.service.slug)
    },
    hasActiveChild() {
      return this.isParentService && this.childServices.some((c) => c.is_active)
    },
    hasDeactivatedChild() {
      return (
        this.isParentService &&
        this.childServices.length > 0 &&
        this.childServices.every((c) => !c.is_active) &&
        this.childServices.some((c) => c.is_deactivated)
      )
    },
    effectiveIsActive() {
      return this.isParentService ? this.hasActiveChild : this.service.is_active
    },
    effectiveIsDeactivated() {
      return this.isParentService ? this.hasDeactivatedChild : this.service.is_deactivated
    },
    statusLabel() {
      if (this.effectiveIsActive) return this.t('ELIXIO_CONNECT.ACTIVE')
      if (this.effectiveIsDeactivated) return this.t('ELIXIO_CONNECT.DEACTIVATED')
      return this.t('ELIXIO_CONNECT.INACTIVE')
    },
    statusClass() {
      if (this.effectiveIsActive) return 'active'
      if (this.effectiveIsDeactivated) return 'deactivated'
      return 'inactive'
    },
  },
  watch: {
    refreshingServiceId(val) {
      this.loading = val != null && val === this.service.id
    },
  },
  methods: {
    openDrawer() {
      this.drawerOpen = true
    },
    handleFormSubmitted(payload) {
      if (payload.success) {
        this.drawerOpen = false
      }
      this.$emit('submitted', payload)
    },
    handleChildSubmitted(payload) {
      if (payload.success && payload.service.is_active) {
        this.$emit('childActivated', {
          parentSlug: this.service.slug,
          childService: payload.service,
        })
      }
      if (payload.success && !payload.service.is_active) {
        this.$emit('childDeactivated', {
          parentSlug: this.service.slug,
          childService: payload.service,
        })
      }
      this.$emit('submitted', payload)
    },
    handleFormCancelled() {
      this.drawerOpen = false
      this.$emit('closed')
    },
  },
}
</script>

<template>
  <div
    class="ec-module-card"
    :class="`ec-module-card--${statusClass}`"
  >
    <!-- Header: logo + info -->
    <div class="ec-module-card__header">
      <div class="ec-module-card__logo">
        <img v-if="service.logo" :src="service.logo" :alt="service.name" />
        <i v-else-if="service.icon" :class="`bi ${service.icon}`" />
      </div>
      <div class="ec-module-card__info">
        <h6 class="ec-module-card__name">{{ service.name }}</h6>
        <span
          class="ec-module-card__status-pill"
          :class="`ec-module-card__status-pill--${statusClass}`"
        >
          {{ statusLabel }}
        </span>
      </div>
    </div>

    <!-- Description -->
    <div class="ec-module-card__body">
      <p v-if="service.description" class="ec-module-card__description">
        {{ service.description }}
      </p>
    </div>

    <!-- Footer: actions -->
    <div class="ec-module-card__footer">
      <EcSpinner v-if="loading" size="sm" />
      <template v-else>
        <div>
          <!-- Parent service: show sub-services -->
          <EcButton
            v-if="isParentService"
            variant="outline-primary"
            size="sm"
            icon="grid"
            @click="openDrawer"
          >
            {{ t('ELIXIO_CONNECT.SHOW_SUB_SERVICES') }}
          </EcButton>
          <!-- Active service: configure -->
          <EcButton
            v-else-if="service.is_active"
            variant="outline-primary"
            size="sm"
            icon="gear"
            @click="openDrawer"
          >
            {{ t('ELIXIO_CONNECT.CONFIGURE') }}
          </EcButton>
          <!-- Deactivated: reactivate -->
          <EcButton
            v-else-if="service.is_deactivated"
            variant="outline-success"
            size="sm"
            icon="power"
            @click="openDrawer"
          >
            {{ t('ELIXIO_CONNECT.REACTIVATE') }}
          </EcButton>
          <!-- Not active: activate -->
          <EcButton
            v-else
            variant="primary"
            size="sm"
            icon="power"
            @click="openDrawer"
          >
            {{ t('ELIXIO_CONNECT.ACTIVATE') }}
          </EcButton>
        </div>
      </template>
    </div>

    <!-- Drawer for configuration -->
    <EcDrawer
      v-model="drawerOpen"
      width="560px"
      resizable
    >
      <template #header>
        <div class="ec-module-card__drawer-header">
          <div class="ec-module-card__drawer-service">
            <div class="ec-module-card__logo ec-module-card__logo--sm">
              <img v-if="service.logo" :src="service.logo" :alt="service.name" />
              <i v-else-if="service.icon" :class="`bi ${service.icon}`" />
            </div>
            <div>
              <strong>{{ service.name }}</strong>
              <span
                class="ec-module-card__status-pill ec-ml-2"
                :class="`ec-module-card__status-pill--${statusClass}`"
              >
                {{ statusLabel }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <!-- Normal service config -->
      <template v-if="!isParentService">
        <ModuleCredentialEditor
          :service="service"
          :organization="organization"
          @submitted="handleFormSubmitted"
          @cancelled="handleFormCancelled"
        />
      </template>

      <!-- Parent service: children list -->
      <template v-else>
        <div class="ec-module-card__children">
          <h6 class="ec-module-card__children-title">
            {{ t('ELIXIO_CONNECT.SUB_SERVICES') }}
          </h6>
          <template v-if="childServices.length > 0">
            <ModuleChildService
              v-for="child in childServices"
              :key="child.slug"
              :service="child"
              :organization="organization"
              :refreshing-service-id="refreshingServiceId"
              @submitted="handleChildSubmitted"
              @closed="handleFormCancelled"
            />
          </template>
          <p v-else class="ec-text-muted ec-text-center ec-py-3" style="font-size: 0.8125rem;">
            {{ t('ELIXIO_CONNECT.NO_SUB_SERVICES') }}
          </p>
        </div>
      </template>
    </EcDrawer>
  </div>
</template>
